import React, { useState, useEffect, useMemo } from "react";
import { Agent } from "../../model_gen/agent_base";
import { RoutingService } from "../../services/RoutingService";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tooltip } from "../ui/tooltip";
import { CheckIcon, AlertCircle, Info } from "lucide-react";
import { cn } from "../../lib/utils";
import RoutingRulesTable from "./RoutingRulesTable";

type RoutingMode = "llm" | "rules";
type RouteTarget = { 
  type: "agent" | "action" | "llm" | "none"; 
  agent_id?: number; 
  action_id?: number; 
};
type RoutingRuleCondition = {
  id?: string | number;
  target_type: "request" | "state";
  variable_path: string;
  operator: "equals" | "not_equals" | "gt" | "gte" | "lt" | "lte" | "like" | "matches" | "sem_match" | "sem_similarity";
  value_text?: string;
  value_number?: number;
  comparison_op?: ">" | ">=" | "=" | "<=" | "<";
  threshold_number?: number;
  enabled?: boolean;
};

type RoutingRule = { 
  id?: string; 
  label?: string;
  if: string; 
  condition_expression_template?: string;
  conditions?: RoutingRuleCondition[];
  route: RouteTarget; 
  enabled?: boolean; 
};
type RoutingConfig = { 
  type: RoutingMode; 
  rules: RoutingRule[]; 
  default?: RouteTarget; 
};

interface AgentRoutingConfigProps {
  agent: Agent | null;
  agentId: string;
  apiKey: string;
  onAgentUpdate: (updatedAgent: Agent) => void;
  orchestrationInstructions: string;
  onOrchestrationEdit: () => void;
  formFields: Array<{ name: string; description: string; type: string; readScope: string; writeScope: string; }>;
}

// Helper function to transform backend rule format to frontend format
const transformBackendRule = (backendRule: any): RoutingRule => {
  // Support both shapes: nested { route: {...} } or flat route_type/route_agent_id/route_action_id
  const route = backendRule.route && typeof backendRule.route === "object"
    ? backendRule.route
    : {
        type: backendRule.route_type,
        agent_id: backendRule.route_agent_id,
        action_id: backendRule.route_action_id,
      };

  return {
    id: backendRule.id,
    label: backendRule.label,
    if: backendRule.condition_expression || backendRule.if,
    condition_expression_template: backendRule.condition_expression_template,
    conditions: backendRule.conditions || [],
    route: {
      type: route?.type,
      agent_id: route?.agent_id != null ? Number(route.agent_id) : undefined,
      action_id: route?.action_id != null ? Number(route.action_id) : undefined,
    },
    enabled: backendRule.enabled,
  };
};

const AgentRoutingConfig: React.FC<AgentRoutingConfigProps> = ({
  agent,
  agentId,
  apiKey,
  onAgentUpdate,
  orchestrationInstructions,
  onOrchestrationEdit,
  formFields,
}) => {
  const [routingMode, setRoutingMode] = useState<RoutingMode>("llm");
  const [routingRules, setRoutingRules] = useState<RoutingRule[]>([]);
  const [routingDefault, setRoutingDefault] = useState<RouteTarget>({ type: "none" });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [initialConfig, setInitialConfig] = useState<RoutingConfig | null>(null);

  // Build flat lookup maps for easy reference
  const agentLookup = useMemo(() => {
    const lookup: Record<number, { name: string; actions: any[] }> = {};
    
    const traverseAgent = (agent: any) => {
      if (agent && agent.id) {
        // Ensure ID is a number for consistent lookup
        const agentId = Number(agent.id);
        lookup[agentId] = {
          name: agent.agent_name || `Agent #${agent.id}`,
          actions: agent.actions || []
        };
        
        // Traverse sub-agents recursively
        if (agent.sub_agents && Array.isArray(agent.sub_agents)) {
          agent.sub_agents.forEach((subAgent: any) => traverseAgent(subAgent));
        }
      }
    };
    
    if (agent) {
      traverseAgent(agent);
    }
    
    return lookup;
  }, [agent]);

  // Load routing config from API on mount/update
  useEffect(() => {
    if (!agent || !agentId || !apiKey) return;
    
    RoutingService.getRoutingConfig(Number(agentId), apiKey).subscribe(
      (response) => {
        try {
          const config: RoutingConfig = {
            type: (response.config.type as RoutingMode) || "llm",
            rules: (response.rules || []).map(transformBackendRule),
            default: response.config.default || { type: "none" }
          };
          
          setRoutingMode(config.type);
          setRoutingRules(config.rules);
          setRoutingDefault(config.default);
          setInitialConfig(config);
        } catch (error) {
          console.error("Error loading routing config:", error);
        }
      },
      (error) => {
        console.error("Error fetching routing config:", error);
      }
    );
  }, [agent, agentId, apiKey]);


  // Check if config has changed
  const isDirty = useMemo(() => {
    if (!initialConfig) return false;
    
    return (
      routingMode !== initialConfig.type ||
      JSON.stringify(routingRules) !== JSON.stringify(initialConfig.rules) ||
      JSON.stringify(routingDefault) !== JSON.stringify(initialConfig.default)
    );
  }, [routingMode, routingRules, routingDefault, initialConfig]);

  // Validate configuration
  const validationError = useMemo(() => {
    // Rules mode requires at least one rule or a default route
    if (routingMode === "rules") {
      if (routingRules.length === 0 && routingDefault.type === "none") {
        return "Rules mode requires at least one rule or a default route";
      }
    }
    return null;
  }, [routingMode, routingRules, routingDefault]);

  const saveRouting = () => {
    if (!agentId || !agent || isSaving || !isDirty || validationError) return;
    
    setIsSaving(true);
    
    const config = {
      type: routingMode,
      default: routingDefault
    };

    // Only save the routing configuration (mode and default route)
    // Rules are already saved individually when added/updated/deleted
    RoutingService.saveRoutingConfig(
      Number(agentId),
      config,
      apiKey
    ).subscribe(
      (response: any) => {
        // Update agent with new routing config
        onAgentUpdate({ 
          ...agent, 
          agent_config: { 
            ...agent.agent_config, 
            routing_config: response.config,
            routing_rules: routingRules // Keep current rules in state
          } 
        } as Agent);
        setIsSaving(false);
        // Update initial config after successful save
        setInitialConfig({
          type: routingMode,
          rules: routingRules,
          default: routingDefault
        });
      },
      (error: any) => {
        console.error("Error saving routing config:", error);
        setIsSaving(false);
      }
    );
  };

  const handleRuleAdd = (newRule: RoutingRule) => {
    console.log("🔍 AgentRoutingConfig - Received rule from editor:", newRule);
    // Save the rule immediately to the database
    RoutingService.createRule(Number(agentId), newRule, apiKey).subscribe(
      (savedRule: any) => {
        console.log("🔍 AgentRoutingConfig - Backend response:", savedRule);
        // Transform backend response to frontend format
        const transformedRule = transformBackendRule(savedRule);
        console.log("🔍 AgentRoutingConfig - Transformed rule:", transformedRule);
        // Only add to local state if database insert was successful
        setRoutingRules(prev => [...prev, transformedRule]);
      },
      (error: any) => {
        console.error("Error creating rule:", error);
        // Don't add to local state if database insert failed
      }
    );
  };

  const handleRuleUpdate = (index: number, updatedRule: RoutingRule) => {
    // If rule has an ID, update it in the database
    if (updatedRule.id) {
      RoutingService.updateRule(Number(agentId), Number(updatedRule.id), updatedRule, apiKey).subscribe(
        (savedRule: any) => {
          // Transform backend response to frontend format
          const transformedRule = transformBackendRule(savedRule);
          // Update local state with saved rule
          setRoutingRules(prev => {
            const copy = [...prev];
            copy[index] = transformedRule;
            return copy;
          });
        },
        (error: any) => {
          console.error("Error updating rule:", error);
          // Don't update local state if database update failed
        }
      );
    } else {
      // Rule doesn't have ID yet, just update local state
      setRoutingRules(prev => {
        const copy = [...prev];
        copy[index] = updatedRule;
        return copy;
      });
    }
  };

  const handleRuleDelete = (index: number) => {
    const ruleToDelete = routingRules[index];
    
    // If rule has an ID, delete it from the database
    if (ruleToDelete.id) {
      RoutingService.deleteRule(Number(agentId), Number(ruleToDelete.id), apiKey).subscribe(
        () => {
          // Remove from local state
          setRoutingRules(prev => prev.filter((_, i) => i !== index));
        },
        (error: any) => {
          console.error("Error deleting rule:", error);
          // Don't remove from local state if database delete failed
        }
      );
    } else {
      // Rule doesn't have ID yet, just remove from local state
      setRoutingRules(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleRuleReorder = (fromIndex: number, toIndex: number) => {
    // Move rule in local state first
    setRoutingRules(prev => {
      const copy = [...prev];
      const [removed] = copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, removed);
      return copy;
    });
    
    // Update rule indices in database
    const reorderedRules = [...routingRules];
    const [moved] = reorderedRules.splice(fromIndex, 1);
    reorderedRules.splice(toIndex, 0, moved);
    
    // Move each rule to its new position
    // The database trigger will handle updating other rules' indices automatically
    reorderedRules.forEach((rule, newIndex) => {
      if (rule.id) {
        RoutingService.reorderRule(Number(agentId), Number(rule.id), newIndex, apiKey).subscribe(
          () => {
            // Single rule reordering successful
          },
          (error: any) => {
            console.error(`Error reordering rule ${rule.id}:`, error);
          }
        );
      }
    });
  };

  return (
    <div className="space-y-6">
      {/* Mode selector */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-base font-gc-med">Routing Mode</Label>
          <p className="text-sm text-muted-foreground">
            Choose how routing decisions are made for this agent
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setRoutingMode("llm")}
            disabled={isSaving}
            className={cn(
              "p-4 border-2 rounded-lg text-left transition-all",
              "hover:border-maitai/50 hover:bg-accent/50",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              routingMode === "llm"
                ? "border-maitai bg-maitai/10"
                : "border-border bg-card"
            )}
          >
            <div className="font-gc-med text-foreground mb-2">LLM Routing</div>
            <div className="text-sm text-muted-foreground">
              Use AI to select the best route based on request content and context
            </div>
          </button>

          <button
            onClick={() => setRoutingMode("rules")}
            disabled={isSaving}
            className={cn(
              "p-4 border-2 rounded-lg text-left transition-all",
              "hover:border-maitai/50 hover:bg-accent/50",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              routingMode === "rules"
                ? "border-maitai bg-maitai/10"
                : "border-border bg-card"
            )}
          >
            <div className="font-gc-med text-foreground mb-2">Rules-Based Routing</div>
            <div className="text-sm text-muted-foreground">
              Evaluate rules in order. First match wins. Configure a fallback when no rules match.
            </div>
          </button>
        </div>
      </div>

      {/* Routing Instructions - only show for LLM mode */}
      {routingMode === "llm" && (
        <Card className="border-border">
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-base font-gc-med">Routing Instructions</Label>
                <Tooltip content="Guide the LLM on how to select routes based on request content, state, and context.">
                  <Info className="w-4 h-4 text-muted-foreground" />
                </Tooltip>
              </div>
              <p className="text-sm text-muted-foreground">
                Optional instructions to guide LLM routing decisions
              </p>
            </div>
            
            <Button
              onClick={onOrchestrationEdit}
              variant="outline"
              className="w-full h-24 justify-start items-start text-left p-4 font-normal"
            >
              <div className="w-full">
                {orchestrationInstructions ? (
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">
                      {orchestrationInstructions.length} characters • {orchestrationInstructions.split("\n").length} lines
                    </div>
                    <div className="text-sm line-clamp-2 text-foreground">
                      {orchestrationInstructions}
                    </div>
                  </div>
                ) : (
                  <div className="text-muted-foreground">
                    Click to add routing instructions...
                  </div>
                )}
              </div>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Rules table - only show for rules mode */}
      {routingMode === "rules" && (
        <RoutingRulesTable
          rules={routingRules}
          onRuleAdd={handleRuleAdd}
          onRuleUpdate={handleRuleUpdate}
          onRuleDelete={handleRuleDelete}
          onRuleReorder={handleRuleReorder}
          subAgents={(agent?.sub_agents || []).map((sa: any) => ({ id: sa.id, agent_name: sa.agent_name }))}
          actions={(agent?.actions || []).map((a: any) => ({ id: a.id, action_name: a.action_name }))}
          formFields={formFields}
          agent={agent}
          apiKey={apiKey}
        />
      )}

      {/* Default route - only show for rules mode */}
      {routingMode === "rules" && (
        <Card className="border-border">
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label className="text-base font-gc-med">Default Route</Label>
              <p className="text-sm text-muted-foreground">
                Used when no rules match. Choose LLM for intelligent fallback, or select a specific agent/action.
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Select 
                value={routingDefault.type}
                onValueChange={(type) => setRoutingDefault({ type: type as RouteTarget["type"] })}
              >
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="llm">LLM</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="agent">Specific Agent</SelectItem>
                  <SelectItem value="action">Specific Action</SelectItem>
                </SelectContent>
              </Select>

              {routingDefault.type === "agent" && (
                <>
                  <Select 
                    value={String(routingDefault.agent_id || "")}
                    onValueChange={(agentId) => setRoutingDefault({ type: "agent", agent_id: Number(agentId) })}
                  >
                    <SelectTrigger className="w-72">
                      <SelectValue placeholder="Select agent..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(agent?.sub_agents || []).map((sa: any) => (
                        <SelectItem key={`agent-${sa.id}`} value={String(sa.id)}>
                          {sa.agent_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Optional action select for default agent route */}
                  {(() => {
                    if (!routingDefault.agent_id) return null;
                    const agentInfo = agentLookup[routingDefault.agent_id];
                    const targetActions = agentInfo ? agentInfo.actions : [];
                    return (
                      <>
                        <Select 
                          value={routingDefault.action_id ? String(routingDefault.action_id) : undefined}
                          onValueChange={(actionId) => setRoutingDefault({ ...routingDefault, type: "agent", action_id: Number(actionId) })}
                        >
                          <SelectTrigger className="w-72">
                            <SelectValue placeholder={targetActions.length ? "Optional: Select action..." : "This agent has no actions"} />
                          </SelectTrigger>
                          <SelectContent>
                            {targetActions.map((a: any) => (
                              <SelectItem key={`action-${a.id}`} value={String(a.id)}>
                                {a.action_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    );
                  })()}
                </>
              )}

              {routingDefault.type === "action" && (
                <Select 
                  value={String(routingDefault.action_id || "")}
                  onValueChange={(actionId) => setRoutingDefault({ type: "action", action_id: Number(actionId) })}
                >
                  <SelectTrigger className="w-72">
                    <SelectValue placeholder="Select action..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(agent?.actions || []).map((a: any) => (
                      <SelectItem key={`action-${a.id}`} value={String(a.id)}>
                        {a.action_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validation errors */}
      {validationError && (
        <div className="flex items-center gap-2 p-4 border border-red-200 bg-red-50 dark:bg-red-950/20 rounded text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{validationError}</span>
        </div>
      )}

      {/* Save button - at bottom */}
    <div className="mt-6 bg-background border-t border-border pt-4 flex items-center justify-between">
      <div className="text-sm text-muted-foreground">
        {isDirty ? (
          "You have unsaved changes"
        ) : (
          "All changes saved"
        )}
      </div>
        <Button 
          onClick={saveRouting} 
          disabled={!isDirty || isSaving || !!validationError}
          className="gap-2"
        >
          <CheckIcon className="w-4 h-4" />
          {isSaving ? "Saving..." : "Save Configuration"}
        </Button>
      </div>
    </div>
  );
};

export default AgentRoutingConfig;
